/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { PriceComponent } from '@msdyn365-commerce/components';
import { ChannelIdentity, Customer, OrderOriginator, OrderShipments, OrgUnitLocation, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import get from 'lodash/get';
import QRCode from 'qrcode.react';
import * as React from 'react';

import { isOboRequest } from '@msdyn365-commerce/core';
import { countItems, IModuleComponentProps, IPriceContext } from './utilities';

/**
 * Interface for order information input resources.
 */
export interface IOrderInformationInputResources {
    receiptEmailMessage?: string;
    orderItemLabel: string;
    orderItemsLabel: string;
    processingLabel: string;
    orderIdLabel: string;
    receiptIdLabel: string;
    confirmationIdLabel: string;
    trackingLabel: string;
    trackingAriaLabel: string;
    trackingComingSoonLabel: string;
    posChannelNameText?: string;
    onlineStoreChannelNameText?: string;
    orderPlacedOnLabel?: string;
    qrCodeSrText?: string;
    callCenterChannelNameText?: string;
    orderPlacedByFullText?: string;
    orderPlacedByYouText?: string;
    orderOnBehalfOfText?: string;
}

/**
 * Interface for order information input.
 */
export interface IOrderInformationInput {
    moduleName: string;
    channelReferenceId?: string;
    channelName?: string;
    channelAddress?: string;
    showChannelInfo?: boolean;
    receiptEmail?: string;
    createdDate?: string;
    amount?: number;
    priceContext: IPriceContext;
    salesId?: string;
    receiptId?: string;
    hasTrackingInfo?: boolean;
    count?: number;
    resources: IOrderInformationInputResources;
    isSalesInvoice?: boolean;
    shouldShowQrCode?: boolean;
    channelTypeValue?: number;
    originator?: OrderOriginator;
    isYou?: [boolean, boolean];
    isB2bAdmin?: boolean;
    isOnlineStore?: boolean;
}

const callCenterChannelTypeValue = 4;

/**
 * Interface for order information.
 */
export interface IOrderInformation {
    orderInformationProps: INodeProps;
    channelName: React.ReactNode;
    channelAddress: React.ReactNode;
    placedBy?: React.ReactNode;
    salesId: React.ReactNode;
    receiptId: React.ReactNode;
    receiptEmail: React.ReactNode;
    createdDate: React.ReactNode;
    count: React.ReactNode;
    amount: React.ReactNode;
    channelReferenceId: React.ReactNode;
}

/**
 * The order confirmation ID node.
 * @param channelReferenceId - The order confirmation id.
 * @param moduleName - The module name.
 * @param confirmationIdLabel - The confirmation label.
 * @param shouldShowQrCode - Should the view show the QR code.
 * @param qrCodeSrText - The screen reader text of the QR code.
 * @returns The react node for order confirmation ID.
 */
const getChannelReference = (
    channelReferenceId: string | undefined,
    moduleName: string,
    confirmationIdLabel: string,
    shouldShowQrCode: boolean | undefined,
    qrCodeSrText?: string
): React.ReactNode | undefined => {
    const confirmationIdNode: React.ReactNode = (
        <span className={`${moduleName}__order-information-channel-reference-id`}>
            {`${confirmationIdLabel} `}
            <span>{channelReferenceId}</span>
        </span>
    );
    return (
        channelReferenceId &&
        (shouldShowQrCode !== undefined && shouldShowQrCode ? (
            <div>
                {confirmationIdNode}
                <div className={`${moduleName}__order-information-qr-code`} aria-labelledby='qrCodeSrText'>
                    <QRCode value={channelReferenceId} size={80} />
                    <span id='qrCodeSrText' className='qrcode-screenreader-text'>
                        {qrCodeSrText}
                    </span>
                </div>
            </div>
        ) : (
            confirmationIdNode
        ))
    );
};

/**
 * Get Channel name to display on order history.
 * @param channelTypeValue - The channel type.
 * @param moduleName - The module name.
 * @param callCenterChannelNameText - The call center channel name.
 * @param channelAddress - Channel Address.
 * @param posChannelNameText - POS channel name.
 * @param channelName - Channel Name.
 * @param onlineStoreChannelNameText - Online store channel name.
 * @returns The react node for channel name label.
 */
export const getChannelName = (
    channelTypeValue: number | undefined,
    moduleName: string,
    callCenterChannelNameText: string | undefined,
    channelAddress: string | undefined,
    posChannelNameText: string | undefined,
    channelName: string | undefined,
    onlineStoreChannelNameText: string | undefined
): React.ReactNode => {
    let channelNameNode: React.ReactNode;
    if (channelTypeValue === callCenterChannelTypeValue) {
        channelNameNode = <span className={`${moduleName}__order-information-channel-name`}>{callCenterChannelNameText}</span>;
    } else if (channelAddress) {
        const posChannelName = `${posChannelNameText ?? ''} ${channelName ?? ''}`;
        channelNameNode = <span className={`${moduleName}__order-information-channel-name channel_hr_pos`}>{posChannelName}</span>;
    } else {
        const onlineChannelName = `${onlineStoreChannelNameText ?? ''} ${channelName ?? ''}`;
        channelNameNode = <span className={`${moduleName}__order-information-channel-name`}>{onlineChannelName}</span>;
    }
    return channelNameNode;
};

const getPlacedBy = (
    moduleName: string,
    originator: OrderOriginator | undefined,
    isYou: [boolean, boolean] | undefined,
    orderPlacedByFullText: string | undefined,
    orderPlacedByYouText: string | undefined,
    orderOnBehalfOfText: string | undefined
): React.ReactNode | undefined => {
    let placedByNode: React.ReactNode | undefined;

    if (originator) {
        const placedByName =
            originator.StaffId && originator.StaffName
                ? originator.StaffName
                : originator.CustomerId && originator.CustomerName
                ? originator.CustomerName
                : '-';
        const onBehalfOfText = originator.StaffId && originator.StaffName && originator.CustomerId && originator.CustomerName;
        placedByNode = (
            <div className={`${moduleName}__sales-order-placed-by`}>
                {orderPlacedByFullText} <span className='bolded'>{placedByName}</span> {isYou && isYou[0] && ` ${orderPlacedByYouText}`}
                {onBehalfOfText && (
                    <div>
                        {orderOnBehalfOfText} <span className='bolded'>{`${originator.CustomerName}`}</span>
                        {isYou && isYou[1] && ` ${orderPlacedByYouText}`}
                    </div>
                )}
            </div>
        );
    }

    return placedByNode;
};

/**
 * Get order information.
 * @param {IOrderInformationInput} param0 The input for order information.
 * @returns {IOrderInformation} The order information.
 */
export const getOrderInformation = ({
    moduleName,
    channelReferenceId,
    channelName,
    channelAddress,
    channelTypeValue,
    showChannelInfo,
    receiptEmail,
    createdDate,
    amount,
    priceContext,
    originator,
    isYou,
    salesId,
    receiptId,
    count,
    resources: {
        receiptEmailMessage,
        orderItemLabel,
        orderItemsLabel,
        orderIdLabel,
        orderPlacedByFullText,
        orderPlacedByYouText,
        orderOnBehalfOfText,
        receiptIdLabel,
        confirmationIdLabel,
        posChannelNameText,
        onlineStoreChannelNameText,
        orderPlacedOnLabel,
        qrCodeSrText,
        callCenterChannelNameText
    },
    isSalesInvoice,
    shouldShowQrCode
}: IOrderInformationInput): IOrderInformation => {
    const channelNameNode: React.ReactNode = getChannelName(
        channelTypeValue,
        moduleName,
        callCenterChannelNameText,
        channelAddress,
        posChannelNameText,
        channelName,
        onlineStoreChannelNameText
    );

    const orderInformation = {
        orderInformationProps: { className: `${moduleName}__order-information` },
        channelName: showChannelInfo && channelName && channelNameNode,
        channelAddress: showChannelInfo && channelAddress && (
            <span className={`${moduleName}__order-information-channel-address`}>{channelAddress}</span>
        ),
        placedBy: getPlacedBy(moduleName, originator, isYou, orderPlacedByFullText, orderPlacedByYouText, orderOnBehalfOfText),
        salesId: salesId ? <span className={`${moduleName}__order-information-sales-id`}>{`${orderIdLabel} ${salesId}`}</span> : '',
        receiptId: receiptId ? (
            <span className={`${moduleName}__order-information-receipt-id`}>{`${receiptIdLabel} ${receiptId}`}</span>
        ) : (
            ''
        ),
        receiptEmail: receiptEmail && (
            <span className={`${moduleName}__order-information-receipt-email`}>
                {receiptEmailMessage}
                <span>{receiptEmail}</span>
            </span>
        ),
        createdDate: !isSalesInvoice && createdDate && (
            <span className={`${moduleName}__order-information-created-date`}>
                {orderPlacedOnLabel}
                {` `}
                {createdDate}
            </span>
        ),
        count: !isSalesInvoice && count !== undefined && count > 0 && (
            <span className={`${moduleName}__order-information-count`}>
                {count === 1 ? `${count} ${orderItemLabel}` : `${count} ${orderItemsLabel}`}
            </span>
        ),
        amount: !isSalesInvoice && (amount || amount === 0) && (
            <PriceComponent
                {...priceContext}
                className={`${moduleName}__order-information-amount`}
                data={{ price: { CustomerContextualPrice: amount } }}
            />
        ),
        channelReferenceId: getChannelReference(channelReferenceId, moduleName, confirmationIdLabel, shouldShowQrCode, qrCodeSrText)
    };

    return orderInformation;
};

/**
 * Interface for order information props.
 */
interface IProps extends IModuleComponentProps {
    resources: IOrderInformationInputResources;
    customer?: Customer;
}

/**
 * Retrieves order channel name.
 * @param order - Order info.
 * @param channelIdentities - Channels data.
 * @returns String with the name of the channel or an empty string.
 */
export const getOrderChannelName: (order: SalesOrder | OrderShipments, channelIdentities?: ChannelIdentity[]) => string = (
    order: SalesOrder | OrderShipments,
    channelIdentities?: ChannelIdentity[]
) => {
    const orderChannel = channelIdentities?.find(channel => {
        return channel.RecordId === order.ChannelId;
    });

    const channelName = orderChannel?.Name ?? '';
    return channelName;
};

/**
 * Retrieves order channel type value.
 * @param order - Order info.
 * @param channelIdentities - Channels data.
 * @returns The channel type value of the order.
 */
export const getOrderChannelTypeValue: (order: SalesOrder | OrderShipments, channelIdentities?: ChannelIdentity[]) => number | undefined = (
    order: SalesOrder | OrderShipments,
    channelIdentities?: ChannelIdentity[]
) => {
    const orderChannel = channelIdentities?.find(channel => {
        return channel.RecordId === order.ChannelId;
    });

    const channelType = orderChannel?.ChannelType.Value;
    return channelType;
};

/**
 * Retrieves channel is online store or not.
 * @param channelTypeValue - The channel type.
 * @param channelAddress - Channel Address.
 * @returns {Boolean} that indicates that the channel is in the online store.
 */
export const isOnlineStoreChannel = (channelTypeValue: number | undefined, channelAddress: string | undefined): boolean => {
    return channelTypeValue !== callCenterChannelTypeValue && !channelAddress;
};

/**
 * Retrieves order channel address.
 * @param order - Order info.
 * @param orgUnitLocations - OrgUnitLocation data.
 * @returns String with the address of the channel or an empty string.
 */
export const getOrderChannelAddress: (order: SalesOrder | OrderShipments, orgUnitLocations?: OrgUnitLocation[]) => string = (
    order: SalesOrder | OrderShipments,
    orgUnitLocations?: OrgUnitLocation[]
) => {
    const orderOrgUnitLocation = orgUnitLocations?.find(location => {
        return location.ChannelId === order.ChannelId;
    });

    const channelAddress = orderOrgUnitLocation?.Address ?? '';
    return channelAddress;
};

/**
 * Get order information input.
 * @param {IProps} props The properties.
 * @param {string} moduleName  The module name.
 * @param {SalesOrder | OrderShipments} order The sales order or order shipments.
 * @param {IPriceContext} priceContext The price context.
 * @param {ChannelIdentity[]} channelIdentities A list of channel identities.
 * @param {OrgUnitLocation[]} orgUnitLocations A list of organization unit locations.
 * @param {Customer} customer A current customer.
 * @param {OrderOriginator} originator The order originator.
 * @param {boolean} showChannelInfo Should the view show channel information.
 * @param {boolean} isSalesInvoice Is this a sales invoice.
 * @param {boolean} shouldShowQrCode Should the view show QR code.
 * @returns {IOrderInformationInput} The order information input.
 */
export const getOrderInformationInput = (
    props: IProps,
    moduleName: string,
    order: SalesOrder | OrderShipments,
    priceContext: IPriceContext,
    channelIdentities?: ChannelIdentity[],
    orgUnitLocations?: OrgUnitLocation[],
    customer?: Customer,
    originator?: OrderOriginator,
    showChannelInfo?: boolean,
    isSalesInvoice?: boolean,
    shouldShowQrCode?: boolean
): IOrderInformationInput => {
    const {
        resources,
        context: {
            request: {
                apiSettings: { channelId }
            }
        }
    } = props;

    // @ts-expect-error OrderShipments doesn't has ChannelId
    const showTrackingInfo = !!order.ChannelId && order.ChannelId === channelId;

    const locale = get(props, 'context.request.locale') || 'en-US';
    const count = order.SalesLines && ArrayExtensions.hasElements(order.SalesLines) ? countItems(order.SalesLines) : 0;
    const channelTypeValue = getOrderChannelTypeValue(order, channelIdentities);
    const channelAddress = getOrderChannelAddress(order, orgUnitLocations);
    const isYou =
        !isOboRequest(props.context.request) &&
        originator?.CustomerId &&
        originator?.CustomerName &&
        !originator?.StaffId &&
        !originator?.StaffName &&
        customer?.AccountNumber === originator?.CustomerId
            ? true
            : false;
    const isYouObo = !isYou && !isOboRequest(props.context.request);

    return {
        moduleName,
        createdDate: (order.CreatedDateTime && new Date(order.CreatedDateTime).toLocaleDateString(locale)) || '',
        salesId: order.SalesId,
        originator: originator,
        isYou: [isYou, isYouObo],
        isB2bAdmin: customer?.IsB2bAdmin,
        receiptId: order.ReceiptId,
        showChannelInfo,

        // @ts-expect-error OrderShipments doesn't has ReceiptEmail
        receiptEmail: order.ReceiptEmail,
        channelReferenceId: order.ChannelReferenceId,
        channelName: getOrderChannelName(order, channelIdentities),
        channelAddress: channelAddress,
        amount: order.TotalAmount ?? 0,
        priceContext,
        count,
        resources,
        isSalesInvoice,
        shouldShowQrCode,
        channelTypeValue: channelTypeValue,
        isOnlineStore: isOnlineStoreChannel(channelTypeValue, channelAddress)
    };
};
